import * as z from 'zod';
import { CACHE_DURATION, deleteContent, generateHash, safeGetItem, safeSetItem } from '@shared/cache/cache.browser';
import type { TileItem } from '@tilelist/domain/composition/TileListitem.ts';

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const SCROLL_POSITION = 'reptile.scrollPosition';

export const scrollPositionSchema = z.preprocess(
    (value) => {
        if (!value) return null;

        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch {
                return null;
            }
        }

        return value;
    },
    z.object({
        save: z.string(),
        listPosition: z.string(),
        timestamp: z.string().regex(/^\d+$/),
        url: z.string(),
    }),
);

type ScrollPositionCache = z.infer<typeof scrollPositionSchema>;

function getCurrentUrl(): string {
    return location.href.replace(location.hash, '');
}

function getCurrentMinutes(): number {
    return Math.floor(new Date().getTime() / 1000 / 60);
}

async function clearScrollPositionCache(): Promise<void> {
    await deleteContent(SCROLL_POSITION);
}

async function getScrollPositionCache(): Promise<ScrollPositionCache | null> {
    const cacheStr = await safeGetItem(SCROLL_POSITION);
    const parsed = scrollPositionSchema.safeParse(cacheStr);

    if (!parsed.success) return null;

    const currentMinutes = getCurrentMinutes();
    const currentUrl = getCurrentUrl();
    const savedTimestamp = Number.parseInt(parsed.data.timestamp, 10);

    const timeValid = currentMinutes - savedTimestamp <= CACHE_DURATION;
    const urlValid = parsed.data.url === currentUrl;

    return timeValid && urlValid ? parsed.data : null;
}

export async function updateScrollPosition(item: TileItem) {
    const cache: ScrollPositionCache = {
        save: `${item.product.id}`,
        listPosition: `${item.localListPosition}`,
        timestamp: `${getCurrentMinutes()}`,
        url: getCurrentUrl(),
    };

    await safeSetItem(SCROLL_POSITION, JSON.stringify(cache));
}

export async function restoreScrollPosition() {
    const cache = await getScrollPositionCache();
    const productTile = cache
        ? document.querySelector(`[data-product-id="${cache.save}"][data-local-list-position="${cache.listPosition}"]`)
        : null;

    if (productTile) {
        /**
 *
 *
 *
 *
 *
 *
 */
        setTimeout(() => {
            productTile.scrollIntoView();
        });
    }

    await clearScrollPositionCache();
}

const SELECTOR_KEY_PREFIX = 'reptile-scroll-selector-';

function selectorStorageKey(): string {
    return SELECTOR_KEY_PREFIX + generateHash(location.href.replace(location.hash, ''));
}

export function findTopVisibleTile(): HTMLElement | null {
    const tiles = document.querySelectorAll<HTMLElement>('.reptile-tile-item');
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    for (const tile of Array.from(tiles)) {
        const rect = tile.getBoundingClientRect();
        if (rect.height <= 0) continue;

        const startsInViewport = rect.top >= 0 && rect.top <= viewportHeight;
        const endsInViewport = rect.bottom >= 0 && rect.bottom <= viewportHeight;
        const occupiesFullScreen = rect.top < 0 && rect.bottom > viewportHeight;

        if (!startsInViewport && !endsInViewport && !occupiesFullScreen) continue;

        const moreThan40PercentVisible = rect.top + rect.height > rect.height * 0.4;
        if (moreThan40PercentVisible) return tile;
    }

    return null;
}

function createSelector(tile: HTMLElement): string | null {
    const { variationId, localListPosition } = tile.dataset;
    if (!variationId || !localListPosition) return null;
    return `[data-variation-id="${variationId}"][data-local-list-position="${localListPosition}"]`;
}

export function saveScrollSelector(): void {
    const tile = findTopVisibleTile();
    if (!tile) return;
    const selector = createSelector(tile);
    if (!selector) return;
    try {
        sessionStorage.setItem(selectorStorageKey(), JSON.stringify({ selector, savedAt: Date.now() }));
    } catch {
        /*                         */
    }
}

/**
 *
 *
 *
 *
 *
 *
 */
export async function restoreBySelector(): Promise<void> {
    let entry: { selector: string; savedAt: number } | null = null;
    try {
        const raw = sessionStorage.getItem(selectorStorageKey());
        if (raw) entry = JSON.parse(raw);
    } catch {
        /*                                           */
    }

    try {
        sessionStorage.removeItem(selectorStorageKey());
    } catch {
        /*                         */
    }

    if (!entry) return;

    const ageMinutes = (Date.now() - entry.savedAt) / 1000 / 60;
    if (ageMinutes > CACHE_DURATION) return;

    document.querySelector(entry.selector)?.scrollIntoView();
}
